import React, { useState } from 'react';
import { 
  ChevronLeft, ThumbsUp, Bookmark, Share2, Eye, 
  MessageSquare, Send, Flag, CheckCircle, XCircle
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { DiscussionDetail } from '@/src/queries/community';
import ReportModal from './ReportModal';
import SolveStatusModal from './SolveStatusModal';

interface DiscussionDetailViewProps {
  selectedDiscussion: DiscussionDetail | null;
  isLoadingDiscussionDetail: boolean;
  allCategories: Array<{id: string, icon: React.ReactNode, name: string}>;
  newComment: string;
  setNewComment: (comment: string) => void;
  setCurrentView: (view: string) => void;
  handleLikeToggle: (type: 'Discussion' | 'Comment', id: string) => void;
  handleBookmarkToggle: (discussionId: string) => void;
  handleCommentSubmit: (discussionId: string) => void;
  handleAuthorClick: (e: React.MouseEvent) => void;
  session: any;
  setShowLoginModal: (show: boolean) => void;
  addCommentMutation: { isPending: boolean };
  // New props for solved functionality
  hasPermission: (permissions: string[]) => boolean; 
  updateDiscussionMutation: { 
    mutate: (params: { id: string, data: any }) => void;
    isPending: boolean;
  };
}

const DiscussionDetailView: React.FC<DiscussionDetailViewProps> = ({
  selectedDiscussion,
  isLoadingDiscussionDetail,
  allCategories,
  newComment,
  setNewComment,
  setCurrentView,
  handleLikeToggle,
  handleBookmarkToggle,
  handleCommentSubmit,
  handleAuthorClick,
  session,
  setShowLoginModal,
  addCommentMutation,
  // New props
  hasPermission,
  updateDiscussionMutation
}) => {
  // Existing state
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportType, setReportType] = useState<'Discussion' | 'Comment'>('Discussion');
  const [reportItemId, setReportItemId] = useState<string>('');
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  
  // New state for solved status modal
  const [solveModalOpen, setSolveModalOpen] = useState(false);
  const [solveAction, setSolveAction] = useState<'solve' | 'unsolve'>('solve');

  // Function to check if user can mark as solved
  const canMarkAsSolved = () => {
    if (!session || !selectedDiscussion) return false;
    return (
      session.user.id === selectedDiscussion.user_id || // Is post owner
      hasPermission(['Administrator']) // Is admin
    );
  };

  // Function to open report modal
  const openReportModal = (type: 'Discussion' | 'Comment', id: string) => {
    if (!session) {
      setShowLoginModal(true);
      return;
    }
    
    setReportType(type);
    setReportItemId(id);
    setReportModalOpen(true);
  };
  
  // Function to handle share button click
  const handleShare = () => {
    // Copy the current URL to clipboard
    navigator.clipboard.writeText(window.location.href);
    
    // Show tooltip
    setShowShareTooltip(true);
    
    // Hide tooltip after 3 seconds
    setTimeout(() => {
      setShowShareTooltip(false);
    }, 3000);
  };

  // Function to open solved status modal
  const openSolveModal = (action: 'solve' | 'unsolve') => {
    if (!canMarkAsSolved()) {
      return;
    }
    
    setSolveAction(action);
    setSolveModalOpen(true);
  };

  // Function to handle solved status update
  const handleSolveStatusUpdate = () => {
    if (!selectedDiscussion) return;
    
    updateDiscussionMutation.mutate({
      id: selectedDiscussion.id,
      data: { 
        is_solved: solveAction === 'solve' 
      }
    });
    
    // Close modal after update is initiated
    setSolveModalOpen(false);
  };

  if (isLoadingDiscussionDetail) {
    return (
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <button 
            className="flex items-center text-indigo-600 font-medium mb-6 hover:text-indigo-800"
            onClick={() => setCurrentView('main')}
          >
            <ChevronLeft className="h-5 w-5 mr-1" />
            Back to discussions
          </button>
          
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
            <h3 className="text-lg font-medium text-gray-900">Loading discussion...</h3>
          </div>
        </div>
      </div>
    );
  }
  
  if (!selectedDiscussion) return null;
  
  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Back button */}
        <button 
          className="flex items-center text-indigo-600 font-medium mb-6 hover:text-indigo-800"
          onClick={() => setCurrentView('main')}
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Back to discussions
        </button>
        
        {/* Discussion header */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div className="p-6">
            <div className="flex items-center mb-4">
              {selectedDiscussion.is_sticky && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 mr-2">
                  Pinned
                </span>
              )}
              {/* Add Solved badge if discussion is solved */}
              {selectedDiscussion.is_solved && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">
                  <CheckCircle className="h-3.5 w-3.5 mr-1" />
                  Solved
                </span>
              )}
              {allCategories.find(c => c.id === selectedDiscussion.category_id)?.icon && (
                <span className="text-gray-500 mr-2">
                  {allCategories.find(c => c.id === selectedDiscussion.category_id)?.icon}
                </span>
              )}
              <h1 className="text-2xl font-bold text-gray-900">{selectedDiscussion.title}</h1>
            </div>
            
            <div className="flex items-center mb-6">
              <div 
                className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-800 font-medium text-xs mr-2 cursor-pointer"
                onClick={handleAuthorClick}
              >
                {selectedDiscussion.user.username?.substring(0, 2).toUpperCase()}
              </div>
              <div>
                <span 
                  className="font-medium text-gray-900 cursor-pointer hover:text-indigo-600"
                  onClick={handleAuthorClick}
                >
                  {selectedDiscussion.user.username}
                </span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  selectedDiscussion.user.experience_level === 'Beginner' ? 'bg-green-100 text-green-800' :
                  selectedDiscussion.user.experience_level === 'Intermediate' ? 'bg-blue-100 text-blue-800' :
                  selectedDiscussion.user.experience_level === 'Advanced' ? 'bg-purple-100 text-purple-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {selectedDiscussion.user.experience_level}
                </span>
                <div className="text-sm text-gray-500 mt-1">
                  Posted {formatDistanceToNow(new Date(selectedDiscussion.created_at), { addSuffix: true })}
                </div>
              </div>
            </div>
            
            <div className="prose max-w-none mb-6">
              {selectedDiscussion.content.split('\n\n').map((paragraph, index) => (
                <p key={index} className="mb-4">{paragraph}</p>
              ))}
            </div>
            
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div className="flex items-center space-x-4">
                <button 
                  className={`flex items-center ${selectedDiscussion.is_liked ? 'text-indigo-600' : 'text-gray-500 hover:text-indigo-600'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLikeToggle('Discussion', selectedDiscussion.id);
                  }}
                >
                  <ThumbsUp className="h-5 w-5 mr-1" />
                  <span>{selectedDiscussion._count?.likes || 0}</span>
                </button>
                <button 
                  className={`flex items-center ${selectedDiscussion.is_bookmarked ? 'text-indigo-600' : 'text-gray-500 hover:text-indigo-600'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBookmarkToggle(selectedDiscussion.id);
                  }}
                >
                  <Bookmark className="h-5 w-5 mr-1" />
                  <span>{selectedDiscussion.is_bookmarked ? 'Saved' : 'Save'}</span>
                </button>
                
                {/* Add Solve/Unsolve button for post owners and admins */}
                {canMarkAsSolved() && (
                  <button 
                    className={`flex items-center ${
                      selectedDiscussion.is_solved 
                        ? 'text-green-600 hover:text-amber-600' 
                        : 'text-gray-500 hover:text-green-600'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      openSolveModal(selectedDiscussion.is_solved ? 'unsolve' : 'solve');
                    }}
                  >
                    {selectedDiscussion.is_solved ? (
                      <>
                        <XCircle className="h-5 w-5 mr-1" />
                        <span>Mark as Unsolved</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-5 w-5 mr-1" />
                        <span>Mark as Solved</span>
                      </>
                    )}
                  </button>
                )}
                
                {/* Updated Share button with tooltip */}
                <div className="relative">
                  <button 
                    className="flex items-center text-gray-500 hover:text-indigo-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShare();
                    }}
                  >
                    <Share2 className="h-5 w-5 mr-1" />
                    <span>Share</span>
                  </button>
                  <div 
                    className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded-md whitespace-nowrap transition-opacity duration-300 ${
                      showShareTooltip ? 'opacity-90' : 'opacity-0 pointer-events-none'
                    }`}
                  >
                    Link copied
                  </div>
                </div>
                
                {/* Report button */}
                <button 
                  className="flex items-center text-gray-500 hover:text-red-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    openReportModal('Discussion', selectedDiscussion.id);
                  }}
                >
                  <Flag className="h-5 w-5 mr-1" />
                  <span>Report</span>
                </button>
              </div>
              <div className="flex items-center text-gray-500">
                <Eye className="h-5 w-5 mr-1" />
                <span>{selectedDiscussion.views} views</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Comments section */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Responses ({selectedDiscussion.comments?.length || 0})
            </h2>
            
            {selectedDiscussion.comments && selectedDiscussion.comments.length > 0 ? (
              <div className="space-y-6">
                {selectedDiscussion.comments.map(comment => (
                  <div key={comment.id} className="border-b border-gray-100 pb-6 last:border-0 last:pb-0">
                    <div className="flex items-start mb-4">
                      <div 
                        className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-800 font-medium text-xs mr-3 cursor-pointer mt-1"
                        onClick={handleAuthorClick}
                      >
                        {comment.user.username?.substring(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center">
                          <span 
                            className="font-medium text-gray-900 cursor-pointer hover:text-indigo-600"
                            onClick={handleAuthorClick}
                          >
                            {comment.user.username}
                          </span>
                          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                            comment.user.experience_level === 'Beginner' ? 'bg-green-100 text-green-800' :
                            comment.user.experience_level === 'Intermediate' ? 'bg-blue-100 text-blue-800' :
                            comment.user.experience_level === 'Advanced' ? 'bg-purple-100 text-purple-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {comment.user.experience_level}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 mb-2">
                          {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                        </div>
                        <div className="text-gray-800">{comment.content}</div>
                        <div className="flex items-center mt-3 space-x-4">
                          <button 
                            className={`flex items-center text-sm ${comment.is_liked ? 'text-indigo-600' : 'text-gray-500 hover:text-indigo-600'}`}
                            onClick={() => handleLikeToggle('Comment', comment.id)}
                          >
                            <ThumbsUp className="h-4 w-4 mr-1" />
                            <span>Like ({comment._count?.likes || 0})</span>
                          </button>
                          <button className="flex items-center text-gray-500 hover:text-indigo-600 text-sm">
                            <MessageSquare className="h-4 w-4 mr-1" />
                            <span>Reply</span>
                          </button>
                          {/* Add Report button for comments */}
                          <button 
                            className="flex items-center text-gray-500 hover:text-red-600 text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openReportModal('Comment', comment.id);
                            }}
                          >
                            <Flag className="h-4 w-4 mr-1" />
                            <span>Report</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No responses yet</h3>
                <p className="text-gray-600 mb-6">
                  Be the first to respond to this discussion.
                </p>
              </div>
            )}
            
            {/* Add comment form */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Add your response</h3>
              
              <div 
                className="border border-gray-300 rounded-lg p-4 focus-within:border-indigo-500 mb-4" 
                onClick={() => !session && setShowLoginModal(true)}
              >
                <textarea
                  className="w-full border-0 p-0 text-gray-800 placeholder-gray-500 outline-none min-h-[100px] "
                  placeholder={session ? "Share your thoughts..." : "Please sign in to comment..."}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  disabled={!session}
                ></textarea>
              </div>
              
              <div className="flex justify-end">
                <button 
                  className={`px-6 py-2 ${
                    addCommentMutation.isPending
                      ? 'bg-indigo-400 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  } text-white rounded-md transition-colors flex items-center`}
                  onClick={() => handleCommentSubmit(selectedDiscussion.id)}
                  disabled={!session || !newComment.trim() || addCommentMutation.isPending}
                >
                  {addCommentMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Posting...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Post Response
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Report Modal */}
        {session && (
          <ReportModal
            isOpen={reportModalOpen}
            onClose={() => setReportModalOpen(false)}
            type={reportType}
            itemId={reportItemId}
            userId={session.user.id}
          />
        )}
        
        {/* Solve Status Modal */}
        {session && (
          <SolveStatusModal
            isOpen={solveModalOpen}
            onClose={() => setSolveModalOpen(false)}
            onConfirm={handleSolveStatusUpdate}
            type={solveAction}
            isPending={updateDiscussionMutation.isPending}
          />
        )}
      </div>
    </div>
  );
};

export default DiscussionDetailView;
